/**
 * Options and the note (docs/ux/design-language.md §7b): one set of states and one note field for
 * every decision — Home blockers, the decision view, approvals, option boards, the phone answer
 * area, batch answers and a run's pinned question.
 *
 * Three states, nothing else:
 *   - **Unfocused**: a plain 1 px box. Every unfocused option looks the same; the suggested one
 *     adds only a small blue "Suggested" pill.
 *   - **Focused** (the keyboard cursor, ↑/↓; it starts on the suggested option): one 2 px focus
 *     ring as an outline, so the box never changes size.
 *   - **Picked**: one strong accent ring, an accent tint, a filled keycap and a check. A picked
 *     option never also shows the focus ring (one ring per element).
 *
 * Nothing shrinks, moves or dims on focus or pick. Every option list has the always-visible note
 * under it (`n`): a pick with a note sends both, and the note alone can be the answer. No "Mix".
 */
import { Check, Mic, MicOff, Paperclip } from 'lucide-react'
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { LARGE_TEXT_BYTES, useAttachmentInput } from '../../lib/attachment-input.js'
import { useDictation } from '../../lib/speech.js'
import type { PendingFile } from '../../lib/uploads.js'
import { AttachmentDropZone, AttachmentNotices } from '../AttachmentDropZone.js'
import { storage } from '../Capture.js'
import { useWide } from '../useWide.js'
import './options.css'

export interface OptionLike {
  id: string
  /** 1-based number (the keycap). */
  key: number
  label: string
  recommended?: boolean
}

/** The keyboard cursor and the picked option of one list. The cursor starts on the suggested. */
export function useOptionCursor(
  options: readonly OptionLike[],
  suggestedId: string | undefined,
  resetKey: string,
) {
  const first = suggestedId ?? options[0]?.id
  const [cursor, setCursor] = useState<string | undefined>(first)
  const [picked, setPicked] = useState<string | undefined>(undefined)
  const [forKey, setForKey] = useState(resetKey)
  if (forKey !== resetKey) {
    setForKey(resetKey)
    setCursor(first)
    setPicked(undefined)
  }
  // Keep the cursor on an option that still exists (a revision can remove options).
  const ids = options.map((o) => o.id)
  const at = cursor && ids.includes(cursor) ? cursor : first
  return {
    cursor: at,
    picked: picked && ids.includes(picked) ? picked : undefined,
    setCursor,
    setPicked,
    /** The option a primary "Pick" acts on: the picked one, else the cursor. */
    target: (picked && ids.includes(picked) ? picked : undefined) ?? at,
    step(dir: 1 | -1) {
      if (!ids.length) return undefined
      const i = at ? ids.indexOf(at) : -1
      const next = ids[(i + dir + ids.length) % ids.length]
      setCursor(next)
      return next
    },
  }
}
export type OptionCursor = ReturnType<typeof useOptionCursor>

/**
 * Props for one option element (any shape: a ballot row, a board tile, a phone answer button).
 * `active`: the list owns the keyboard cursor, so the cursor ring shows without DOM focus.
 */
export function optionState(
  o: OptionLike,
  s: { cursor?: string | undefined; picked?: string | undefined; active?: boolean },
) {
  const picked = s.picked === o.id
  const cursor = !!s.active && s.cursor === o.id && !picked
  return {
    picked,
    cursor,
    className: `vk-opt${cursor ? ' is-cursor' : ''}${picked ? ' is-picked' : ''}`,
  }
}

/** The number keycap; filled with a check once the option is picked. */
export function OptionKey({ n, picked }: { n: number; picked: boolean }) {
  return (
    <span className="vk-opt__key" aria-hidden="true">
      {picked ? <Check size={14} strokeWidth={2.5} /> : n}
    </span>
  )
}

/** The only mark a suggested option gets. */
export function SuggestedPill() {
  return <span className="vk-opt__pill">Suggested</span>
}

/** Arrow keys on a focused option: move the cursor (and DOM focus) to the neighbour. */
export function moveOnArrow(
  e: KeyboardEvent<HTMLElement>,
  cur: OptionCursor,
  opts: { horizontal?: boolean } = {},
) {
  const back = e.key === 'ArrowUp' || (opts.horizontal && e.key === 'ArrowLeft')
  const fwd = e.key === 'ArrowDown' || (opts.horizontal && e.key === 'ArrowRight')
  if (!back && !fwd) return false
  e.preventDefault()
  const next = cur.step(fwd ? 1 : -1)
  const group = e.currentTarget.closest('[role="radiogroup"]')
  requestAnimationFrame(() =>
    group?.querySelector<HTMLElement>(`[data-option-id="${CSS.escape(next ?? '')}"]`)?.focus(),
  )
  return true
}

// ---- the note -------------------------------------------------------------------------------------

export interface PickNote {
  note?: string
  files?: PendingFile[]
}

/** The note under every option list (text, files, pasted screenshots, dictation). */
export function useNote() {
  const [text, setText] = useState('')
  const input = useAttachmentInput({
    storageOff: storage.off,
    largeTextBytes: LARGE_TEXT_BYTES,
    insertText: (t) => setText((cur) => cur + t),
  })
  const dictation = useDictation((t) =>
    setText((cur) => (cur.trim() ? `${cur.replace(/\s+$/, '')} ${t}` : t)),
  )
  const field = useRef<HTMLTextAreaElement>(null)
  const started = !!text.trim() || input.files.length > 0
  return {
    text,
    setText,
    input,
    dictation,
    field,
    started,
    focus: () => field.current?.focus(),
    /** Files that failed to read or are too large block sending until removed. */
    blocked: !input.canSend,
    extra: (): PickNote | undefined =>
      started
        ? {
            ...(text.trim() ? { note: text.trim() } : {}),
            ...(input.files.length ? { files: input.files } : {}),
          }
        : undefined,
    clear: () => {
      setText('')
      input.clear()
    },
  }
}
export type Note = ReturnType<typeof useNote>

export const NOTE_PLACEHOLDER = 'Add a note: what to change or combine…'
/** Phones: the placeholder fits one line at 390 (the hint under the field says the rest). */
export const NOTE_PLACEHOLDER_SHORT = 'Add a note…'

/**
 * The always-visible note. ⏎ picks the target option with the note, ⇧⏎ is a new line, ⌘↵ sends
 * the note on its own (the whole answer), Esc leaves the field and keeps the draft.
 */
export function NoteField({
  note,
  target,
  busy,
  onPickWithNote,
  onNoteOnly,
  showKeys = true,
  files = true,
  placeholder,
  hint: hintText,
}: {
  note: Note
  /** The option a pick would send ("30 seconds"); absent when the decision has no options. */
  target?: string | undefined
  busy: boolean
  onPickWithNote?: (() => void) | undefined
  onNoteOnly?: (() => void) | undefined
  showKeys?: boolean
  /** Offer attachments (lists that answer in bulk carry text only). */
  files?: boolean
  /** Composer modes (Answer, New request) say what the field sends. */
  placeholder?: string
  hint?: string
}) {
  const hid = useId()
  const wide = useWide()
  const d = note.dictation
  const hint = hintText
    ? hintText
    : note.started
      ? target
        ? `Sent with your pick: ${target} · or send the note on its own`
        : 'Sent as your answer'
      : target
        ? 'Goes with the option you pick, or on its own'
        : 'Your answer, in your words'
  return (
    <AttachmentDropZone input={note.input} className="vk-note dv-note">
      <label htmlFor={`${hid}-n`} className="sr-only">
        Note
      </label>
      <div className="vk-note__box">
        <textarea
          id={`${hid}-n`}
          ref={note.field}
          rows={1}
          value={d.interim ? `${note.text} ${d.interim}` : note.text}
          placeholder={
            placeholder ??
            (target ? (wide ? NOTE_PLACEHOLDER : NOTE_PLACEHOLDER_SHORT) : 'Write your answer…')
          }
          aria-describedby={`${hid}-h`}
          aria-keyshortcuts="n"
          onChange={(e) => note.setText(e.target.value)}
          onPaste={note.input.onPaste}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault()
              if (!busy && note.started) (onNoteOnly ?? onPickWithNote)?.()
            } else if (e.key === 'Enter' && !e.shiftKey && !e.altKey) {
              e.preventDefault()
              if (!busy) (target ? onPickWithNote : onNoteOnly)?.()
            }
            if (e.key === 'Escape') e.currentTarget.blur()
          }}
        />
        {files ? (
          <label className="vk-note__tool" title="Attach files">
            <Paperclip size={16} aria-hidden="true" />
            <span className="sr-only">Attach</span>
            <input
              type="file"
              multiple
              hidden
              onChange={(e) => {
                note.input.add(e.target.files)
                e.target.value = ''
              }}
            />
          </label>
        ) : null}
        {d.supported ? (
          <button
            type="button"
            className="vk-note__tool"
            aria-pressed={d.listening}
            aria-label={d.listening ? 'Stop dictation' : 'Dictate'}
            onClick={d.toggle}
          >
            {d.listening ? (
              <MicOff size={16} aria-hidden="true" />
            ) : (
              <Mic size={16} aria-hidden="true" />
            )}
          </button>
        ) : null}
        {showKeys && !note.started ? (
          <span className="vk-note__key" aria-hidden="true">
            <kbd className="ak">n</kbd>
          </span>
        ) : null}
      </div>
      <p className="vk-note__hint" id={`${hid}-h`}>
        {hint}
      </p>
      <AttachmentNotices input={note.input} />
      {note.input.items.length ? (
        <ul className="dv-note__files" aria-label="Attached to the note">
          {note.input.items.map((f) => (
            <li key={f.id}>
              <span>{f.name}</span>
              <button
                type="button"
                className="dv-link"
                aria-label={`Remove ${f.name}`}
                onClick={() => note.input.remove(f.id)}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </AttachmentDropZone>
  )
}

/** `n` focuses the note of the decision that owns the keyboard (never while typing). */
export function useNoteKey(note: Note, enabled: boolean) {
  const ref = useRef(note)
  ref.current = note
  useEffect(() => {
    if (!enabled) return
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key !== 'n' || e.metaKey || e.ctrlKey || e.altKey) return
      const t = e.target as HTMLElement | null
      if (t?.closest('input, textarea, select, [contenteditable="true"]')) return
      if (document.querySelector('[role="dialog"][aria-modal="true"]')) return
      e.preventDefault()
      e.stopImmediatePropagation()
      ref.current.focus()
    }
    addEventListener('keydown', onKey, true)
    return () => removeEventListener('keydown', onKey, true)
  }, [enabled])
}
